import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { captureAPI } from '../services/api';
import { useIsAdmin } from '../identity/useIsAdmin';
import './VendorDirectoryPage.css';

/**
 * Vendor Directory (slice 2b-2): the tenant's own vendor records, edited like a
 * spreadsheet. A document whose printed vendor name normalises to one of a
 * vendor's printed names carries that vendor's display name and number into
 * exports (capture-service services/vendorRecord). With no records nothing
 * changes anywhere.
 *
 * Writes are admin-only on the server (accessPolicy vendors:write); everyone
 * else sees the table read-only. Rows keep their place while being edited:
 * only a reload, a new search or a new sort re-orders them.
 *
 * URL: ?vendor=<id> scrolls to and highlights a row; ?new=1&printed_name=<name>
 * opens the add row with that printed name (admins).
 *
 * Slice 2c-2: the table shows 100 rows a page, client side (ruling 11; GET
 * /vendors is not paged yet). Once any vendor came from an import, each value
 * says whether it is from the file or yours, and an imported vendor shows its
 * source:id. Admins reach the import at /vendor-directory/import.
 */

// Server limits (capture-app services/vendorRecord/records.ts).
export const DISPLAY_NAME_MAX = 200;
export const VENDOR_NUMBER_MAX = 64;
export const PRINTED_NAME_MAX = 200;

export const PAGE_SIZE = 100;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
  { value: 'documents_desc', label: 'Most documents' },
];

const DASH = '—';

/** The name a row goes by: its display name, else its first printed name. */
export const vendorLabel = (v) => v?.display_name || v?.names?.[0]?.printed_name || '';

const documentCountText = (v) => (typeof v.document_count === 'number' ? String(v.document_count) : DASH);

function matches(v, q) {
  if (!q) return true;
  const has = (s) => typeof s === 'string' && s.toLowerCase().includes(q);
  return has(v.display_name) || has(v.vendor_number) || has(v.external_id) || (v.names || []).some((n) => has(n.printed_name));
}

/** Filtered and sorted ids, computed only on load, search and sort. */
export function orderVendorIds(vendors, search, sortBy) {
  const q = search.trim().toLowerCase();
  const list = vendors.filter((v) => matches(v, q));
  list.sort((a, b) => {
    switch (sortBy) {
      case 'name_desc': return vendorLabel(b).localeCompare(vendorLabel(a));
      // A count that could not be read sorts last.
      case 'documents_desc': return (b.document_count ?? -1) - (a.document_count ?? -1);
      default: return vendorLabel(a).localeCompare(vendorLabel(b));
    }
  });
  return list.map((v) => v.id);
}

/**
 * Profiles whose printed vendor name has no vendor record yet, one per
 * normalised key (a vendor can have a profile per document type).
 */
export function vendorsWithoutRecord(profiles, vendors) {
  const known = new Set();
  for (const v of vendors) for (const n of v.names || []) known.add(n.vendor_name_normalized);
  const out = new Map();
  for (const p of profiles) {
    const key = p.vendor_name_normalized;
    if (!key || !key.trim() || known.has(key) || out.has(key)) continue;
    out.set(key, { key, printed_name: p.vendor_name });
  }
  return [...out.values()].sort((a, b) => a.printed_name.localeCompare(b.printed_name));
}

/**
 * The message for a failed write. Returns { text, ownerId?, ownerName?, printed? }
 * so a name_taken message can link to the vendor that owns the name.
 */
export function vendorErrorMessage(err, printed) {
  const body = err?.response?.data;
  switch (body?.code) {
    case 'name_taken':
      return { kind: 'name_taken', printed, ownerId: body.vendor_id || null, ownerName: body.vendor_display_name || null };
    case 'empty_key':
      return { text: `“${printed}” has no letters or numbers to match on.` };
    case 'invalid':
      return { text: body.error || 'That value was not accepted.' };
    case 'not_found':
      return { text: 'This vendor was deleted. Reloading.', reload: true };
    default:
      return { text: 'Could not save. Try again.' };
  }
}

/** "from file" or "yours" under a value (slice 2c, ruling 2 of 2c: origin per field). */
function OriginMark({ value, origin }) {
  if (value === null || value === undefined || value === '') return null;
  return <span className={`vd-origin vd-origin-${origin === 'file' ? 'file' : 'manual'}`}>{origin === 'file' ? 'from file' : 'yours'}</span>;
}

function ErrorText({ error }) {
  if (!error) return null;
  if (error.kind === 'name_taken') {
    const owner = error.ownerName || 'another vendor';
    return (
      <div className="vd-row-error" role="alert">
        {`“${error.printed}” already belongs to `}
        {error.ownerId ? <Link to={`/vendor-directory?vendor=${error.ownerId}`}>{owner}</Link> : owner}
      </div>
    );
  }
  return <div className="vd-row-error" role="alert">{error.text}</div>;
}

/**
 * A cell edited in place. Enter or blur saves; Escape puts the saved value back
 * and saves nothing; an unchanged value (after trimming) saves nothing; an
 * emptied input saves null. `onSave(next)` resolves true on success.
 */
export function EditableCell({ value, maxLength, onSave, disabled, label }) {
  const saved = value ?? '';
  const [draft, setDraft] = useState(saved);
  const focused = useRef(false);
  const skipBlur = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(saved);
  }, [saved]);

  const commit = async () => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === (value ?? null)) {
      setDraft(saved);
      return;
    }
    const ok = await onSave(next);
    if (!ok) setDraft(saved);
  };

  return (
    <input
      className="vd-cell-input"
      aria-label={label}
      value={draft}
      maxLength={maxLength}
      disabled={disabled}
      onFocus={() => { focused.current = true; }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          skipBlur.current = true;
          commit();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          skipBlur.current = true;
          setDraft(saved);
          e.currentTarget.blur();
        }
      }}
      onBlur={() => {
        focused.current = false;
        if (skipBlur.current) {
          skipBlur.current = false;
          return;
        }
        commit();
      }}
    />
  );
}

/** Printed names as chips; admins remove with ✕ and add with Enter. */
function PrintedNameChips({ vendor, isAdmin, disabled, onAdd, onRemove }) {
  const [draft, setDraft] = useState('');
  const names = vendor.names || [];
  return (
    <div className="vd-chips">
      {names.map((n) => (
        <span key={n.id} className="vd-chip">
          {n.printed_name}
          {isAdmin && (
            <button
              type="button"
              className="vd-chip-remove"
              aria-label={`Remove ${n.printed_name}`}
              disabled={disabled}
              onClick={() => onRemove(n)}
            >
              ✕
            </button>
          )}
        </span>
      ))}
      {!isAdmin && names.length === 0 && <span className="vd-muted">{DASH}</span>}
      {isAdmin && (
        <input
          className="vd-chip-input"
          aria-label={`Add printed name to ${vendorLabel(vendor) || 'vendor'}`}
          placeholder="+ name"
          value={draft}
          maxLength={PRINTED_NAME_MAX}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === 'Escape') {
              setDraft('');
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const printed = draft.trim();
              if (!printed) return;
              if (await onAdd(printed)) setDraft('');
            }
          }}
        />
      )}
    </div>
  );
}

function AddVendorRow({ initial, saving, error, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const keys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onSave(form); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  return (
    <tr className={`vd-row vd-add-row ${saving ? 'vd-saving' : ''}`} data-testid="vd-add-row">
      <td>
        <input className="vd-cell-input vd-cell-input-boxed" aria-label="New vendor display name" placeholder="Display name"
          value={form.display_name} maxLength={DISPLAY_NAME_MAX} disabled={saving} onChange={set('display_name')} onKeyDown={keys} />
      </td>
      <td>
        <input className="vd-cell-input vd-cell-input-boxed" aria-label="New vendor number" placeholder="Vendor number"
          value={form.vendor_number} maxLength={VENDOR_NUMBER_MAX} disabled={saving} onChange={set('vendor_number')} onKeyDown={keys} />
      </td>
      <td>
        <input className="vd-cell-input vd-cell-input-boxed" aria-label="New vendor printed name" placeholder="Printed name"
          value={form.printed_name} maxLength={PRINTED_NAME_MAX} disabled={saving} onChange={set('printed_name')} onKeyDown={keys} />
        <ErrorText error={error} />
      </td>
      <td />
      <td className="vd-td-actions">
        <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={() => onSave(form)}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" className="btn btn-sm" disabled={saving} onClick={onCancel}>Cancel</button>
      </td>
    </tr>
  );
}

const EMPTY_ADD = { display_name: '', vendor_number: '', printed_name: '' };

export default function VendorDirectoryPage() {
  const isAdmin = useIsAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadSeq, setLoadSeq] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [orderedIds, setOrderedIds] = useState([]);
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [rowErrors, setRowErrors] = useState({});
  const [adding, setAdding] = useState(null); // the add row's initial values, or null
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, loading, affected }
  const [highlightId, setHighlightId] = useState(null);
  const [page, setPage] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const rowRefs = useRef({});
  const vendorsRef = useRef(vendors);
  vendorsRef.current = vendors;

  const load = useCallback(() => {
    setLoading(true);
    captureAPI.getVendors()
      .then((res) => {
        if (res?.ok) {
          setVendors(res.vendors || []);
          setLoadError(null);
        } else {
          setLoadError('Failed to load vendors');
        }
      })
      .catch(() => setLoadError('Failed to load vendors'))
      .finally(() => {
        setLoading(false);
        setLoadSeq((n) => n + 1);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Profiles feed the "seen without a record" list (admins only). A failed read hides it.
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    captureAPI.getVendorProfiles()
      .then((res) => { if (!cancelled && res?.ok) setProfiles(res.profiles || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Re-order only on load, search and sort, so a row being edited stays put.
  useEffect(() => {
    setOrderedIds(orderVendorIds(vendorsRef.current, search, sortBy));
    setPage(0);
  }, [loadSeq, search, sortBy]);

  const vendorParam = searchParams.get('vendor');
  useEffect(() => {
    if (!vendorParam || loadSeq === 0) return;
    if (!vendorsRef.current.some((v) => v.id === vendorParam)) return;
    setSearch((s) => (s && !orderVendorIds(vendorsRef.current, s, 'name_asc').includes(vendorParam) ? '' : s));
    setHighlightId(vendorParam);
  }, [vendorParam, loadSeq]);

  // A highlighted row on another page brings its page into view.
  useEffect(() => {
    if (!highlightId) return;
    const at = orderedIds.indexOf(highlightId);
    if (at >= 0) setPage(Math.floor(at / PAGE_SIZE));
  }, [highlightId, orderedIds]);

  useEffect(() => {
    if (!highlightId) return;
    const el = rowRefs.current[highlightId];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightId, orderedIds]);

  // ?new=1&printed_name=… opens the add row once, then leaves the URL.
  const newParam = searchParams.get('new');
  const printedParam = searchParams.get('printed_name');
  useEffect(() => {
    if (!isAdmin || newParam !== '1') return;
    setAddError(null);
    setAdding({ ...EMPTY_ADD, printed_name: printedParam || '' });
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    next.delete('printed_name');
    setSearchParams(next, { replace: true });
  }, [isAdmin, newParam, printedParam, searchParams, setSearchParams]);

  const byId = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v])), [vendors]);
  const rows = orderedIds.map((id) => byId[id]).filter(Boolean);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageAt = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(pageAt * PAGE_SIZE, (pageAt + 1) * PAGE_SIZE);
  const showOrigins = vendors.some((v) => v.external_id);
  const withoutRecord = useMemo(() => (isAdmin ? vendorsWithoutRecord(profiles, vendors) : []), [isAdmin, profiles, vendors]);

  const markSaving = (id, on) => setSavingIds((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const setRowError = (id, error) => setRowErrors((prev) => ({ ...prev, [id]: error }));

  /** Replace a row from a write's response; the response has no document_count, so keep the row's. */
  const replaceRow = (vendor) => setVendors((prev) => prev.map((v) => (
    v.id === vendor.id ? { ...vendor, document_count: v.document_count } : v
  )));

  const runWrite = async (id, printed, call) => {
    markSaving(id, true);
    try {
      const res = await call();
      if (res?.vendor) replaceRow(res.vendor);
      setRowError(id, null);
      return true;
    } catch (err) {
      const message = vendorErrorMessage(err, printed);
      setRowError(id, message);
      if (message.reload) load();
      return false;
    } finally {
      markSaving(id, false);
    }
  };

  const saveField = (vendor, field) => (next) =>
    runWrite(vendor.id, null, () => captureAPI.updateVendor(vendor.id, { [field]: next }));

  const addName = (vendor) => (printed) =>
    runWrite(vendor.id, printed, () => captureAPI.addVendorName(vendor.id, printed));

  const removeName = (vendor) => (name) =>
    runWrite(vendor.id, name.printed_name, () => captureAPI.removeVendorName(vendor.id, name.id));

  const openAdd = (printedName = '') => {
    setAddError(null);
    setAdding({ ...EMPTY_ADD, printed_name: printedName });
  };

  const saveNew = async (form) => {
    const display_name = form.display_name.trim();
    const vendor_number = form.vendor_number.trim();
    const printed = form.printed_name.trim();
    if (!display_name && !vendor_number && !printed) {
      setAddError({ text: 'Enter a display name, vendor number or printed name.' });
      return;
    }
    setAddSaving(true);
    try {
      const res = await captureAPI.createVendor({
        display_name: display_name || null,
        vendor_number: vendor_number || null,
        printed_names: printed ? [printed] : [],
      });
      const vendor = { ...res.vendor, document_count: 0 };
      setVendors((prev) => [vendor, ...prev]);
      setOrderedIds((prev) => [vendor.id, ...prev]);
      setAdding(null);
      setAddError(null);
      setHighlightId(vendor.id);
    } catch (err) {
      setAddError(vendorErrorMessage(err, printed));
    } finally {
      setAddSaving(false);
    }
  };

  const startDelete = async (vendor) => {
    setConfirmDelete({ id: vendor.id, loading: true, affected: null });
    try {
      const res = await captureAPI.getVendor(vendor.id);
      const affected = typeof res?.documents_affected === 'number' ? res.documents_affected : null;
      setConfirmDelete((c) => (c?.id === vendor.id ? { ...c, loading: false, affected } : c));
    } catch {
      setConfirmDelete((c) => (c?.id === vendor.id ? { ...c, loading: false, affected: null } : c));
    }
  };

  const doDelete = async (vendor) => {
    markSaving(vendor.id, true);
    try {
      await captureAPI.deleteVendor(vendor.id);
      setVendors((prev) => prev.filter((v) => v.id !== vendor.id));
      setOrderedIds((prev) => prev.filter((id) => id !== vendor.id));
      setConfirmDelete(null);
    } catch (err) {
      const message = vendorErrorMessage(err, null);
      setRowError(vendor.id, message.reload ? message : { text: 'Could not delete. Try again.' });
      setConfirmDelete(null);
      if (message.reload) load();
    } finally {
      markSaving(vendor.id, false);
    }
  };

  const confirmText = (vendor, c) => {
    const name = vendorLabel(vendor) || 'this vendor';
    if (typeof c.affected !== 'number') {
      return `Delete ${name}? Its unpublished documents will lose its name and number. Published documents keep theirs.`;
    }
    const docs = c.affected === 1 ? '1 unpublished document' : `${c.affected} unpublished documents`;
    return `Delete ${name}? ${docs} will lose its name and number. Published documents keep theirs.`;
  };

  return (
    <div className="vd-page">
      <div className="vd-page-header">
        <h2>Vendor Directory</h2>
        <p className="vd-page-subtitle">
          Your own name and vendor number for each vendor, applied to matching documents.
        </p>
      </div>

      {loading && vendors.length === 0 && <div className="vd-loading">Loading vendors...</div>}
      {loadError && <div className="vd-error">{loadError}</div>}

      {!isAdmin && !loading && (
        <p className="vd-readonly-note">Only tenant admins can change vendor records.</p>
      )}

      {isAdmin && withoutRecord.length > 0 && (
        <details className="vd-unrecorded" data-testid="vd-unrecorded">
          <summary>Vendors seen without a record ({withoutRecord.length})</summary>
          <ul>
            {withoutRecord.map((u) => (
              <li key={u.key}>
                <span>{u.printed_name}</span>
                <button type="button" className="btn btn-sm" onClick={() => openAdd(u.printed_name)}>Add</button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {!loading && !loadError && vendors.length === 0 && !adding && (
        <div className="vd-empty" data-testid="vd-empty">
          <p>
            Add the vendors you work with, with your own name and vendor number for each. Documents whose printed
            vendor name matches one of a vendor&apos;s printed names carry those values into exports.
          </p>
          {isAdmin && (
            <div className="vd-empty-actions">
              <button type="button" className="btn btn-primary" onClick={() => openAdd()}>Add vendor</button>
              <Link to="/vendor-directory/import" className="btn">Import from a file</Link>
            </div>
          )}
        </div>
      )}

      {(vendors.length > 0 || adding) && (
        <>
          <div className="vd-toolbar">
            <input
              type="text"
              className="vd-search"
              placeholder="Search vendors..."
              aria-label="Search vendors"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className="vd-sort-select" aria-label="Sort vendors" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {search && (
              <span className="vd-result-count">
                {rows.length} of {vendors.length} vendor{vendors.length !== 1 ? 's' : ''}
              </span>
            )}
            {isAdmin && !adding && (
              <button type="button" className="btn btn-primary btn-sm vd-add-btn" onClick={() => openAdd()}>Add vendor</button>
            )}
            {isAdmin && (
              <Link to="/vendor-directory/import" className="btn btn-sm vd-import-btn">Import</Link>
            )}
          </div>

          <table className="vd-table">
            <thead>
              <tr>
                <th>Display name</th>
                <th className="vd-th-number">Vendor number</th>
                <th>Printed names</th>
                <th className="vd-th-docs" title="Documents carrying this vendor now, published included">Documents</th>
                <th className="vd-th-actions" />
              </tr>
            </thead>
            <tbody>
              {adding && (
                <AddVendorRow
                  initial={adding}
                  saving={addSaving}
                  error={addError}
                  onSave={saveNew}
                  onCancel={() => { setAdding(null); setAddError(null); }}
                />
              )}
              {pageRows.map((v) => {
                const saving = savingIds.has(v.id);
                const c = confirmDelete?.id === v.id ? confirmDelete : null;
                return (
                  <tr
                    key={v.id}
                    ref={(el) => { rowRefs.current[v.id] = el; }}
                    data-testid={`vd-row-${v.id}`}
                    className={['vd-row', saving ? 'vd-saving' : '', highlightId === v.id ? 'vd-row-highlight' : ''].filter(Boolean).join(' ')}
                  >
                    <td>
                      {isAdmin ? (
                        <EditableCell
                          label={`Display name for ${vendorLabel(v) || 'vendor'}`}
                          value={v.display_name}
                          maxLength={DISPLAY_NAME_MAX}
                          disabled={saving}
                          onSave={saveField(v, 'display_name')}
                        />
                      ) : (v.display_name || <span className="vd-muted">{DASH}</span>)}
                      {showOrigins && <OriginMark value={v.display_name} origin={v.display_name_origin} />}
                      {v.external_id && (
                        <span className="vd-external" title="Source and id from the import">{`${v.external_source}:${v.external_id}`}</span>
                      )}
                    </td>
                    <td>
                      {isAdmin ? (
                        <EditableCell
                          label={`Vendor number for ${vendorLabel(v) || 'vendor'}`}
                          value={v.vendor_number}
                          maxLength={VENDOR_NUMBER_MAX}
                          disabled={saving}
                          onSave={saveField(v, 'vendor_number')}
                        />
                      ) : (v.vendor_number || <span className="vd-muted">{DASH}</span>)}
                      {showOrigins && <OriginMark value={v.vendor_number} origin={v.vendor_number_origin} />}
                    </td>
                    <td>
                      <PrintedNameChips vendor={v} isAdmin={isAdmin} disabled={saving} onAdd={addName(v)} onRemove={removeName(v)} />
                      <ErrorText error={rowErrors[v.id]} />
                      {c && (
                        <div className="vd-delete-confirm" role="alertdialog" aria-label="Confirm delete">
                          <span className="vd-delete-confirm-text">
                            {c.loading ? 'Checking documents...' : confirmText(v, c)}
                          </span>
                          <button type="button" className="vd-danger-btn" disabled={c.loading || saving} onClick={() => doDelete(v)}>
                            {saving ? 'Deleting...' : 'Yes, delete'}
                          </button>
                          <button type="button" className="vd-cancel-btn" disabled={saving} onClick={() => setConfirmDelete(null)}>Cancel</button>
                        </div>
                      )}
                    </td>
                    <td className="vd-td-docs">{documentCountText(v)}</td>
                    <td className="vd-td-actions">
                      {isAdmin && !c && (
                        <button type="button" className="vd-delete-btn" aria-label={`Delete ${vendorLabel(v) || 'vendor'}`} disabled={saving} onClick={() => startDelete(v)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && vendors.length > 0 && (
                <tr><td colSpan={5} className="vd-no-results">No vendors match your search.</td></tr>
              )}
            </tbody>
          </table>
          {rows.length > PAGE_SIZE && (
            <div className="vd-pager">
              <button type="button" className="btn btn-sm" disabled={pageAt === 0} onClick={() => setPage(pageAt - 1)}>Previous</button>
              <span className="vd-pager-text">
                {`Vendors ${pageAt * PAGE_SIZE + 1} to ${Math.min(rows.length, (pageAt + 1) * PAGE_SIZE)} of ${rows.length}`}
              </span>
              <button type="button" className="btn btn-sm" disabled={pageAt >= pageCount - 1} onClick={() => setPage(pageAt + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
